// Per-character color gradients across text, for the Gradient Generator. Pure.
//
// Where each output works (none of the § / & forms is vanilla — see HexStyle in formatCodes.ts):
//   - MiniMessage `<gradient>` and `<#RRGGBB>`: Paper / Adventure only. `<gradient>` is
//     interpolated by the server itself, so it can differ slightly from the preview.
//   - `&#RRGGBB`: EssentialsX / CMI-style plugins. `&x&R…` and `§x§R…`: Spigot, Paper, BungeeCord.
//   - The text component: vanilla, 1.16+ (hex `color`). Rendered per version by the caller
//     through renderComponentArgument — JSON before 1.21.5, SNBT after.
import { hexToRGB } from '@/app/lib/minecraft/font';
import { SECTION, escapeMiniMessage } from '@/app/lib/minecraft/formatCodes';

export const HEX_COLOR = /^#[0-9A-F]{6}$/i;

/** Typed commands and chat messages stop at 256 characters. */
export const CHAT_LIMIT = 256;

export interface GradientStyle {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  /** Give spaces no color code of their own (shorter output). Ignored with underline/strike,
   *  where a space's color is visible. */
  skipSpaces: boolean;
}

export const PLAIN_STYLE: GradientStyle = { bold: false, italic: false, underline: false, strike: false, skipSpaces: true };

/** One visible character (a grapheme) and its color; `hex` null = no code of its own. */
export interface Cell { ch: string; hex: string | null }

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('').toUpperCase();
}

// Evenly interpolate the color stops across `length` positions → per-index hex.
export function interpolate(stops: string[], length: number): string[] {
  if (length <= 0) return [];
  const rgb = stops.filter(s => HEX_COLOR.test(s)).map(hexToRGB);
  if (rgb.length === 0) return Array(length).fill('#FFFFFF');
  if (rgb.length === 1) return Array(length).fill(rgbToHex(...rgb[0]));

  const out: string[] = [];
  for (let i = 0; i < length; i++) {
    const t = length === 1 ? 0 : i / (length - 1); // 0..1 across the whole string
    const seg = t * (rgb.length - 1);
    const idx = Math.min(Math.floor(seg), rgb.length - 2);
    const lt = seg - idx;
    const a = rgb[idx];
    const b = rgb[idx + 1];
    out.push(rgbToHex(
      Math.round(a[0] + (b[0] - a[0]) * lt),
      Math.round(a[1] + (b[1] - a[1]) * lt),
      Math.round(a[2] + (b[2] - a[2]) * lt),
    ));
  }
  return out;
}

/** Split into graphemes, so a ZWJ emoji or a combining accent keeps one color. */
export function graphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map(s => s.segment);
  }
  return [...text];
}

const spacesVisible = (s: GradientStyle) => s.underline || s.strike;

export function gradientCells(text: string, stops: string[], style: GradientStyle): Cell[] {
  const chars = graphemes(text);
  const skip = style.skipSpaces && !spacesVisible(style);
  const colored = (ch: string) => !(skip && /^\s+$/.test(ch));
  const hexes = interpolate(stops, chars.filter(colored).length);
  let k = 0;
  return chars.map(ch => ({ ch, hex: colored(ch) ? hexes[k++] : null }));
}

const FORMAT_LETTERS: [keyof GradientStyle, string, string][] = [
  ['bold', 'l', 'bold'], ['italic', 'o', 'italic'], ['underline', 'n', 'underlined'], ['strike', 'm', 'strikethrough'],
];
const formatCodes = (prefix: string, s: GradientStyle) =>
  FORMAT_LETTERS.filter(([k]) => s[k]).map(([, c]) => prefix + c).join('');
const miniTags = (s: GradientStyle) => FORMAT_LETTERS.filter(([k]) => s[k]).map(([, , tag]) => tag);

/** `&` or `§` codes. A color code clears formatting, so the formats follow every color. */
export function toLegacyGradient(cells: Cell[], style: GradientStyle, prefix: '&' | typeof SECTION, hex: 'hash' | 'legacy'): string {
  const fmt = formatCodes(prefix, style);
  let out = '';
  let prev: string | null = null;
  for (const { ch, hex: h } of cells) {
    if (h && h !== prev) {
      out += (hex === 'hash' ? prefix + h : prefix + 'x' + h.slice(1).split('').map(d => prefix + d).join('')) + fmt;
      prev = h;
    } else if (!h && prev === null && fmt) {
      // A leading uncolored space still needs the formats in force.
      out += fmt;
      prev = '';
    }
    out += ch;
  }
  return out;
}

/** MiniMessage `<gradient>`: short, but the SERVER interpolates (Paper / Adventure). */
export function toMiniMessageGradient(text: string, stops: string[], style: GradientStyle): string {
  const tags = miniTags(style);
  const valid = stops.filter(s => HEX_COLOR.test(s));
  const body = valid.length >= 2 ? `<gradient:${valid.join(':')}>${escapeMiniMessage(text)}</gradient>` : escapeMiniMessage(text);
  return tags.map(t => `<${t}>`).join('') + body + [...tags].reverse().map(t => `</${t}>`).join('');
}

/**
 * MiniMessage with one `<#RRGGBB>` per character: exactly the preview's colors. The color tags are
 * never closed (each one overrides the last), so the format tags are left open too rather than
 * closed across them; an unclosed tag runs to the end of the string.
 */
export function toMiniMessagePerChar(cells: Cell[], style: GradientStyle): string {
  let out = miniTags(style).map(t => `<${t}>`).join('');
  let prev: string | null = null;
  for (const { ch, hex } of cells) {
    if (hex && hex !== prev) { out += `<${hex}>`; prev = hex; }
    out += escapeMiniMessage(ch);
  }
  return out;
}

interface Comp { text: string; color?: string; bold?: true; italic?: true; underlined?: true; strikethrough?: true; extra?: Comp[] }

/**
 * The text-component VALUE: formats once on a root, one child per color. Pass it to
 * renderComponentArgument (version-correct), never JSON.stringify for a command.
 */
export function toGradientComponent(cells: Cell[], style: GradientStyle): Comp {
  const extra: Comp[] = [];
  for (const { ch, hex } of cells) {
    const last = extra[extra.length - 1];
    if (last && (hex === null || hex === last.color)) last.text += ch;
    else extra.push(hex ? { text: ch, color: hex } : { text: ch });
  }
  const root: Comp = { text: '' };
  if (style.bold) root.bold = true;
  if (style.italic) root.italic = true;
  if (style.underline) root.underlined = true;
  if (style.strike) root.strikethrough = true;
  if (extra.length) root.extra = extra;
  return root;
}
